// REST-Client für die Regelwerk-Doc-Routen des maco-runner.
// Teilt das Auth/Fehler-Muster mit changelogApi.ts.

import type { AhbDoc, MigDoc, DocsIndex } from "@/types/regelwerk";
import type { StandEntry } from "@/types";

export class RegelwerkApiError extends Error {
  constructor(public status: number, public detail: string) {
    super(`Regelwerk-API (${status}): ${detail}`);
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const { getToken } = await import("./auth");
  const token = getToken();
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    ...((init?.headers as Record<string, string> | undefined) ?? {}),
  };
  if (token) headers.Authorization = `Bearer ${token}`;
  const res = await fetch(path, { ...init, headers });
  if (!res.ok) {
    let detail = res.statusText;
    try {
      const body = await res.json();
      detail = body?.detail ?? detail;
    } catch {
      /* nicht JSON */
    }
    throw new RegelwerkApiError(res.status, detail);
  }
  return res.json() as Promise<T>;
}

export interface DocIndex {
  doctypes: Record<string, { formatversions: string[] }>;
}

const key = (s: string) => encodeURIComponent(s);

export const regelwerkApi = {
  /** Welche Doctypes/Formatversionen existieren überhaupt. */
  getDocIndex: () => request<DocIndex>("/api/changelog/doc-index"),
  /** Formate + Prüfids einer Formatversion (für Picker + Globale Suche). */
  listDocs: (fv: string, stand?: string) =>
    request<DocsIndex>(`/api/changelog/docs/${key(fv)}${stand ? `?stand=${stand}` : ""}`),
  /** Voller AHB-Prüfid (lines + pruefungen + pakete). */
  getAhbDoc: (fv: string, fmt: string, pruefid: string, stand?: string) =>
    request<AhbDoc>(
      `/api/changelog/doc/ahb/${key(fv)}/${key(fmt)}/${key(pruefid)}${stand ? `?stand=${stand}` : ""}`,
    ),
  /** Volle MIG (verschachtelte segments). */
  getMigDoc: (fv: string, fmt: string, stand?: string) =>
    request<MigDoc>(
      `/api/changelog/doc/mig/${key(fv)}/${key(fmt)}${stand ? `?stand=${stand}` : ""}`,
    ),
  /** Verfügbare Stände für einen Doctype/Formatversion. */
  getStaende: (doctype: "ahb" | "mig", fv: string) =>
    request<Record<string, { current: string; staende: StandEntry[] }>>(
      `/api/changelog/staende/${doctype}/${key(fv)}`,
    ),
};
